import React, { Suspense, useEffect, useState, useRef } from "react";

import homePagePayload from "../../config/homePayload";
import homePageAPI from "../../api/homePageApi";

import CouponFlash from "./CouponFlash";
import { Link } from "react-router-dom";
import { Row, Col, Container } from "react-bootstrap";

import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "./Shared/CustomSwiper";
import { FaBox, FaCheckCircle, FaHeadset } from "react-icons/fa";
import { Clock, Heart, ShieldCheck, Truck } from "lucide-react";

const Product = React.lazy(() => import("../products/Card"));
const Testimonials = React.lazy(() => import("../testimonials/Card"));
const CategoryCard = React.lazy(() => import("../categories/Card"));
const Blog = React.lazy(() => import("../blogs/Card"));
const Banner = React.lazy(() => import("./Banner"));
const BrandsCard = React.lazy(() => import("../brands/Card"));

function useIsPhone(maxWidth = 767) {
  const [isPhone, setIsPhone] = useState(
    typeof window !== "undefined" &&
      window.matchMedia(`(max-width: ${maxWidth}px)`).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (e) => setIsPhone(e.matches);
    setIsPhone(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [maxWidth]);
  return isPhone;
}

function Home() {
  const isPhone = useIsPhone();
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [products, setProducts] = useState([]);
  const [topSellerProducts, setTopSellerProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [middleBanners, setMiddleBanners] = useState([]);
  const firstMiddleBanner = middleBanners[0];
  const secondMiddleBanner = middleBanners[1];
  const [brands, setBrands] = useState([]);

  const parallaxRef = useRef(null);
const [parallaxOffset, setParallaxOffset] = useState(0);

const parallaxRef2 = useRef(null);
const [parallaxOffset2, setParallaxOffset2] = useState(0);

useEffect(() => {
  const handleScroll = () => {
    if (parallaxRef.current) {
      const rect = parallaxRef.current.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const distance = viewportCenter - elementCenter;
      setParallaxOffset(distance * 0.15);
    }

    if (parallaxRef2.current) {
      const rect2 = parallaxRef2.current.getBoundingClientRect();
      const viewportCenter2 = window.innerHeight / 2;
      const elementCenter2 = rect2.top + rect2.height / 2;
      const distance2 = viewportCenter2 - elementCenter2;
      setParallaxOffset2(distance2 * 0.15);
    }
  };

  window.addEventListener("scroll", handleScroll, { passive: true });
  return () => window.removeEventListener("scroll", handleScroll);
}, []);

  useEffect(() => {
    fetchHomePageDetails();
  }, []);

  const CACHE_TIME = 3 * 60 * 1000;

  const applyData = (data) => {
    setTopBanners(data?.banners?.filter((b) => b.position === "top"));
    setMiddleBanners(data?.banners?.filter((b) => b.position === "middle"));
    setCoupons(data?.coupons);
    setCategories(data?.categories);
    setTestimonials(data?.testimonials);
    setBlogs(data?.blogs);
    setProducts(data?.products);
    setTopSellerProducts(data?.topSellerProducts);
    setBrands(data?.brands);
  };

  const fetchHomePageDetails = async () => {
    try {
      const cached = sessionStorage.getItem("homeData");
      const parsed = cached ? JSON.parse(cached) : null;

      if (parsed) applyData(parsed.data);

      if (!parsed || Date.now() - parsed.timestamp > CACHE_TIME) {
        const res = await homePageAPI.homePage(homePagePayload);
        if (res.code === 200) {
          sessionStorage.setItem(
            "homeData",
            JSON.stringify({ data: res.data, timestamp: Date.now() }),
          );
          if (!parsed) applyData(res.data); // only update state if no cache was shown
        }
      }
    } catch (error) {
      console.error("Home API Error:", error);
    }
  };

  // featured section
  const features = [
    {
      id: 1,
      icon: <Truck size={40} strokeWidth={1.5} className="text-danger1" />,
      title: "Fast & Reliable Delivery",
      description: "Same day delivery in 50+ cities with real-time tracking.",
    },
    {
      id: 2,
      icon: <Heart size={40} strokeWidth={1.5} className="text-danger1" />,
      title: "Curated with Love",
      description: "Each hamper is hand-picked and styled to perfection.",
    },
    {
      id: 3,
      icon: (
        <ShieldCheck size={40} strokeWidth={1.5} className="text-danger1" />
      ),
      title: "Secure Payments",
      description: "100% safe checkout with multiple payment options.",
    },
    {
      id: 4,
      icon: <Clock size={40} strokeWidth={1.5} className="text-danger1" />,
      title: "24/7 Support",
      description: "Dedicated team to help you with your gifting needs.",
    },
  ];

  return (
    <>
      

      {/* show banners */}
      {topBanners && topBanners.length > 0 && (
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          slidesPerView={1}
          pagination={{ clickable: true }}
          autoplay={{ delay: 3000 }}
          loop={true}
        >
          {topBanners.map((banner, index) => (
            <SwiperSlide key={index}>
              <Banner item={banner} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      {/* brands section */}
      {/* {brands && brands.length > 0 && (
  <div className="ax-brand-marquee-wrapper py-4" data-aos="fade-up">
    <div className="ax-brand-marquee-track">
      {[...brands, ...brands].map((brand, index) => (
        <div className="ax-brand-marquee-item" key={index}>
          <BrandsCard item={brand} />
        </div>
      ))}
    </div>
  </div>
)} */}

      {/* Coupon Bar */}
      {coupons && coupons.length > 0 && (
        <section className="custom-coupon-section">
          <div className="custom-coupon-marquee-wrap">
            <div className="custom-coupon-marquee">
              {coupons.map((coupon, index) => (
                <CouponFlash key={coupon._id || index} item={coupon} />
              ))}

              {/* DUPLICATE FOR SMOOTH LOOP */}
              {coupons.map((coupon, index) => (
                <CouponFlash
                  key={`duplicate-${coupon._id || index}`}
                  item={coupon}
                />
              ))}
            </div>
          </div>
        </section>
      )}
        {/* featured section */}
     {/* <section className="custom-featured-section">
  <div className="container">
    <div className="row g-4 justify-content-center">
      {features.map((item) => (
        <div className="col-6 col-md-3 text-center" key={item.id}>
          <div className="why-card">
            <div className="icon-wrapper">{item.icon}</div>
            <h6 className="why-card-title">{item.title}</h6>
            <p className="why-card-desc">{item.description}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
</section> */}
      {/* category listing */}
     {categories && categories.length > 0 && (
  <section className="mosaic-section ">
    
    <div className="d-flex justify-content-between align-items-end mb-4 px-2 py-4">
              <div className="mosaic-section-heading">
      <span className="mosaic-heading-tag">Explore</span>
      <h2 className="mosaic-heading-title">
        Our <span>Category</span>
      </h2>
    </div>
              <Link
                to="/categories"
                className="text-decoration-none fw-semibold"
                style={{ color: "var(--ivorea-primary-dark)" }}
              >
                View All →
              </Link>
            </div>

    {isPhone ? (
      <Swiper
        modules={[Pagination, Autoplay]}
        slidesPerView={1.15}
        spaceBetween={12}
        centeredSlides={false}
        pagination={{ clickable: true }}
        autoplay={{ delay: 3500, disableOnInteraction: false }}
        className="category-mobile-swiper"
      >
        {categories.map((category, index) => (
          <SwiperSlide key={category._id || index}>
            <Suspense fallback={<div></div>}>
              <CategoryCard item={category} />
            </Suspense>
          </SwiperSlide>
        ))}
      </Swiper>
    ) : (
      <div className="mosaic-banner-wrapper">
        {categories.map((category, index) => (
          <CategoryCard key={category._id || index} item={category} />
        ))}
      </div>
    )}
  </section>
)}

      {/* bestsellers */}
      {products && products.length > 0 && (
        <section className="py-5 product-section">
          <Container fluid className="px-md-5 px-2">
               <div className="d-flex justify-content-between align-items-end mb-4 px-2">
              <div className="mosaic-section-heading ">
      <span className="mosaic-heading-tag">Explore</span>
      <h2 className="mosaic-heading-title">
        Our <span>Products</span>
      </h2>
    </div>
              <Link
                to="/shop"
                className="text-decoration-none fw-semibold"
                style={{ color: "var(--ivorea-primary-dark)" }}
              >
                View All →
              </Link>
            </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {products.map((product, index) => (
                  <Col
                    lg={3}
                    md={4}
                    sm={6}
                    xs={6}
                    key={[product._id || index]}
                  >
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </Container>
        </section>
      )}

      {/* middle banners */}
     {firstMiddleBanner && (
  <div className="cta-split-banner" ref={parallaxRef}>
    <div className="cta-split-left">
      <span className="cta-split-tag">
        <span className="cta-tag-line"></span>
        {firstMiddleBanner.tag || "Heritage Artificial Jewels"}
      </span>

      <h2 className="cta-split-heading">{firstMiddleBanner.title}</h2>

      <p className="cta-split-desc">{firstMiddleBanner.subTitle}</p>

      <Link to="/shop" className="cta-split-btn">
        Explore Collection
      </Link>
    </div>

    <div className="cta-split-right">
      <img
        src={firstMiddleBanner.image}
        alt={firstMiddleBanner.title}
        className="cta-split-img"
        style={{ transform: `translateY(${parallaxOffset}px)` }}
      />
    </div>
  </div>
)}

      {/* trending */}
      {topSellerProducts && topSellerProducts.length > 0 && (
        <section className="py-5  top-sellers-section">
          <Container fluid className="px-md-5 px-2">
           <div className="d-flex justify-content-between align-items-end mb-4 px-2">
              <div className="mosaic-section-heading ">
      <span className="mosaic-heading-tag">Our Favourites</span>
      <h2 className="mosaic-heading-title">
        Best <span>Sellers</span>
      </h2>
    </div>
              <Link
                to="/shop"
                className="text-decoration-none fw-semibold"
                style={{ color: "var(--ivorea-primary-dark)" }}
              >
                View All →
              </Link>
            </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {topSellerProducts.map((product, index) => (
                  <Col
                    lg={3}
                    md={6}
                    sm={6}
                    xs={6}
                    key={[product._id || index]}
                  >
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </Container>
        </section>
      )}

       {/* second middle banner - full width overlay */}
{/* second middle banner - split layout (image left, text right) */}
{secondMiddleBanner && (
  <div className="cta-split-banner cta-split-reverse" ref={parallaxRef2}>
    <div className="cta-split-right">
      <img
        src={secondMiddleBanner.image}
        alt={secondMiddleBanner.title}
        className="cta-split-img"
        style={{ transform: `translateY(${parallaxOffset2}px)` }}
      />
    </div>

    <div className="cta-split-left">
      <span className="cta-split-tag">
        <span className="cta-tag-line"></span>
        {secondMiddleBanner.tag || "Curated Collection"}
      </span>

      <h2 className="cta-split-heading">{secondMiddleBanner.title}</h2>

      <p className="cta-split-desc">
        {secondMiddleBanner.subTitle || secondMiddleBanner.sub_title}
      </p>

      <Link to="/shop" className="cta-split-btn">
        Discover More
      </Link>
    </div>
  </div>
)}

      

      {testimonials && testimonials.length > 0 && (
        <section className="py-5 overflow-hidden testimonial-section">
          <div className="container">
            {/* Section Header */}
           <div className="mosaic-section-heading">
      <span className="mosaic-heading-tag">TestimonialS</span>
      <h2 className="mosaic-heading-title">
        What Our <span>Customers</span> Says
      </h2>
    </div>

            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 2500 }}
              breakpoints={{
                0: { slidesPerView: 1.2 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="testimonial-swiper py-3"
            >
              <Suspense fallback={<div></div>}>
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={index}>
                    <Testimonials item={testimonial}  />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </div>
        </section>
      )}

     

    

 {blogs && blogs.length > 0 && (
        <section className="blog-section  py-5">
          <Container fluid className="px-md-5 px-2">
            <div className="d-flex justify-content-between align-items-end mb-4 px-2">
              <div className="mosaic-section-heading">
      <span className="mosaic-heading-tag">Our Blogs</span>
      <h2 className="mosaic-heading-title">
        Our Latest <span>Stories</span>
      </h2>
    </div>
              <Link
                to="/blog"
                className="text-decoration-none fw-semibold"
                style={{ color: "var(--ivorea-primary-dark)" }}
              >
                View All →
              </Link>
            </div>

            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="blog-swiper"
            >
              <Suspense fallback={<div></div>}>
                {blogs.map((blog, index) => (
                  <SwiperSlide key={blog.id}>
                    <Blog item={blog} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
         </Container>
        </section>
      )}
    </>
  );
}

export default Home;
